import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import api from './api/index';
import { AppShell } from './components/AppShell';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { BillingPage } from './pages/BillingPage';
import { ContactUsPage } from './pages/ContactUsPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { CustomersPage } from './pages/CustomersPage';
import { DashboardPage } from './pages/DashboardPage';
import { EnvTaxPage } from './pages/EnvTaxPage';
import { FitnessPage } from './pages/FitnessPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { HelpPage } from './pages/HelpPage';
import { InsurancesPage } from './pages/InsurancesPage';
import { LicencesPage } from './pages/LicencesPage';
import { LoginPage } from './pages/LoginPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { PUCsPage } from './pages/PUCsPage';
import { ReferralPage } from './pages/ReferralPage';
import { RegisterPage } from './pages/RegisterPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { RoadTaxPage } from './pages/RoadTaxPage';
import { SettingsPage } from './pages/SettingsPage';
import SuperAdminAnalytics from './pages/SuperAdminAnalytics';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminSettings from './pages/SuperAdminSettings';
import SuperAdminSubscriptions from './pages/SuperAdminSubscriptions';
import { TeamPage } from './pages/TeamPage';
import { VehicleDetailPage } from './pages/VehicleDetailPage';
import { VehiclesPage } from './pages/VehiclesPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { useAuthStore } from './store/auth';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      refetchOnMount: 'always',
      refetchInterval: 60 * 1000,
      retry: 1,
    },
  },
})

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isHydrated = useAuthStore((s) => s.isHydrated)

  // Show nothing while hydrating (don't redirect to login yet)
  if (!isHydrated) return null

  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

function RegularUserRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const isHydrated = useAuthStore((s) => s.isHydrated)

  // Show nothing while hydrating
  if (!isHydrated) return null

  if (!isAuthenticated) return <Navigate to="/login" replace />
  
  // Superadmins should not access regular user routes
  if (user?.role === 'superadmin') return <Navigate to="/superadmin/analytics" replace />
  
  return <>{children}</>
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const isHydrated = useAuthStore((s) => s.isHydrated)

  // Show nothing while hydrating
  if (!isHydrated) return null

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (user?.role !== 'superadmin') return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function DashboardRedirect() {
  const user = useAuthStore((s) => s.user)
  
  // Redirect superadmins to their dashboard
  if (user?.role === 'superadmin') {
    return <Navigate to="/superadmin/analytics" replace />
  }
  
  // Redirect regular users to the regular dashboard
  return <Navigate to="/dashboard" replace />
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const isHydrated = useAuthStore((s) => s.isHydrated)

  // Show nothing while hydrating (don't redirect to dashboard yet)
  if (!isHydrated) return null

  if (isAuthenticated) {
    // Redirect superadmins to their dashboard
    if (user?.role === 'superadmin') {
      return <Navigate to="/superadmin/analytics" replace />
    }
    // Redirect regular users to regular dashboard
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

function App() {
  const isHydrated = useAuthStore((s) => s.isHydrated)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  useEffect(() => {
    // After hydration, try to refresh token if we have authentication
    if (!isHydrated) return

    if (isAuthenticated) {
      // Attempt to refresh the token silently on app load using the api instance
      const refreshToken = useAuthStore.getState().refreshToken
      const body = refreshToken ? { refresh_token: refreshToken } : {}
      api
        .post('/auth/refresh', body)
        .then(({ data }) => {
          // Update BOTH access and refresh tokens from response
          useAuthStore.getState().setTokens({
            access_token: data.access_token,
            refresh_token: data.refresh_token,
          })
        })
        .catch(() => {
          // If refresh fails, clear auth state (token might be expired)
          useAuthStore.getState().clear()
        })
    }
  }, [isHydrated, isAuthenticated])

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route
            path="/verify-email"
            element={<VerifyEmailPage />}
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage />}
          />
          <Route
            path="/accept-invite"
            element={<AcceptInvitePage />}
          />

          {/* Protected routes — wrapped in AppShell (regular users only) */}
          <Route
            path="/"
            element={
              <RegularUserRoute>
                <AppShell />
              </RegularUserRoute>
            }
          >
            <Route index element={<DashboardRedirect />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="team" element={<TeamPage />} />

            {/* Customers */}
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/:id" element={<CustomerDetailPage />} />
            <Route
              path="customers/:customerId/vehicles/:vehicleId"
              element={<VehicleDetailPage />}
            />

            {/* Vehicles */}
            <Route path="vehicles" element={<VehiclesPage />} />

            {/* Documents */}
            <Route path="licences" element={<LicencesPage />} />
            <Route path="insurances" element={<InsurancesPage />} />
            <Route path="pucs" element={<PUCsPage />} />
            <Route path="fitness" element={<FitnessPage />} />
            <Route path="road-taxes" element={<RoadTaxPage />} />
            <Route path="env-taxes" element={<EnvTaxPage />} />

            {/* Account */}
            <Route path="billing" element={<BillingPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="referral" element={<ReferralPage />} />
            <Route path="settings" element={<SettingsPage />} />

            {/* Support */}
            <Route path="help" element={<HelpPage />} />
            <Route path="contact-us" element={<ContactUsPage />} />
          </Route>

          {/* Super Admin routes */}
          <Route
            path="/superadmin/analytics"
            element={
              <SuperAdminRoute>
                <SuperAdminAnalytics />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/superadmin/dashboard"
            element={
              <SuperAdminRoute>
                <SuperAdminDashboard />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/superadmin/subscriptions"
            element={
              <SuperAdminRoute>
                <SuperAdminSubscriptions />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/superadmin/settings"
            element={
              <SuperAdminRoute>
                <SuperAdminSettings />
              </SuperAdminRoute>
            }
          />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </QueryClientProvider>
  )
}

export default App
